import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

import { useAuth } from '../../context/AuthContext';
import ComposePanel from '../../pages/client-mail/ComposePanel';
import { announceChange, openCompose } from '../../pages/client-mail/ClientMailShell';

/**
 * CLIENT MAIL → COMPOSE, FROM ANY PAGE.
 *
 * A round button that floats over every screen for the people who may write
 * client mail (`user.clientMailAccess` — admins and the DPM coordinator
 * department). It opens the same ComposePanel as Client mail → Compose, so
 * the email still goes to the Approval Queue; nothing is sent from here.
 *
 * On a Client mail page the page already owns a composer, so the button asks
 * that one to open (`openCompose`) rather than stacking a second panel.
 *
 * It can be dragged anywhere and remembers where (per browser, a convenience
 * only — the default corner is used whenever storage does not work). A press
 * that moves under DRAG_THRESHOLD pixels is a click; past it, a drag, and the
 * click that follows is swallowed. Move and release are watched on `window`,
 * not with pointer capture, so the button's own click is never retargeted.
 * The position is clamped on every drag and window resize, so a spot
 * remembered on a wide monitor cannot strand it off a laptop screen.
 */

const STORAGE_KEY = 'spoonfeed.widget.client-mail-compose';
const SIZE = 52;
const MARGIN = 16;
const DRAG_THRESHOLD = 4;
/* Above the Help button in the bottom-right corner, not on top of it. */
const DEFAULT_OFFSET = { right: 24, bottom: 88 };

function readStored(fallback) {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    return typeof parsed?.x === 'number' && typeof parsed?.y === 'number' ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function store(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* Private window, or site data blocked. The button still works. */
  }
}

const clamp = (p) => ({
  x: Math.min(Math.max(MARGIN, p.x), Math.max(MARGIN, window.innerWidth - SIZE - MARGIN)),
  y: Math.min(Math.max(MARGIN, p.y), Math.max(MARGIN, window.innerHeight - SIZE - MARGIN)),
});

export default function ComposeLauncher() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [pos, setPos] = useState(null);
  const [compose, setCompose] = useState(null);
  const drag = useRef({ active: false, moved: false });
  const handlers = useRef({ move: null, up: null });

  useEffect(() => {
    setPos(clamp(readStored({
      x: window.innerWidth - SIZE - DEFAULT_OFFSET.right,
      y: window.innerHeight - SIZE - DEFAULT_OFFSET.bottom,
    })));
    const onResize = () => setPos((p) => (p ? clamp(p) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const detach = useCallback(() => {
    const { move, up } = handlers.current;
    if (move) window.removeEventListener('pointermove', move);
    if (up) {
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    }
    handlers.current = { move: null, up: null };
  }, []);
  useEffect(() => detach, [detach]);

  if (!user?.clientMailAccess || !pos) return null;

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    const box = e.currentTarget.getBoundingClientRect();
    drag.current = { active: true, moved: false, dx: e.clientX - box.left, dy: e.clientY - box.top, startX: e.clientX, startY: e.clientY };
    const move = (ev) => {
      if (!drag.current.active) return;
      if (Math.hypot(ev.clientX - drag.current.startX, ev.clientY - drag.current.startY) > DRAG_THRESHOLD) drag.current.moved = true;
      if (!drag.current.moved) return;
      ev.preventDefault();
      setPos(clamp({ x: ev.clientX - drag.current.dx, y: ev.clientY - drag.current.dy }));
    };
    const up = () => {
      detach();
      drag.current.active = false;
      if (drag.current.moved) setPos((p) => { if (p) store(p); return p; });
    };
    handlers.current = { move, up };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const onClick = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      drag.current.moved = false;
      return;
    }
    if (pathname.startsWith('/client-mail')) openCompose();
    else setCompose({ key: Date.now() });
  };

  return (
    <>
      {!compose && (
        <button
          type="button"
          aria-label="Compose client email"
          title="Compose client email · drag to move"
          onPointerDown={onPointerDown}
          onClick={onClick}
          style={{ left: pos.x, top: pos.y, width: SIZE, height: SIZE, touchAction: 'none' }}
          className="fixed z-[60] flex cursor-grab select-none items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg transition-shadow hover:shadow-xl hover:shadow-brand-600/30 active:cursor-grabbing"
        >
          <PencilSquareIcon className="h-6 w-6" />
        </button>
      )}
      {compose && (
        <ComposePanel
          key={compose.key}
          onClose={() => setCompose(null)}
          onSubmitted={announceChange}
        />
      )}
    </>
  );
}
